import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import {
  createRefundPolicy,
  getRefundPolicy,
  updateRefundPolicy,
  type CreateRefundPolicyDto,
  type RefundPolicy,
  type RefundPolicyRule,
  type UpdateRefundPolicyDto,
} from '../lib/api-client';

interface RuleFormRow {
  hours_before_departure_gte: string;
  refund_ratio_pct: string; // UI 用百分比輸入（0..100）→ 送出時 / 100
  fixed_fee: string;
}

interface FormState {
  name: string;
  description: string;
  active: boolean;
  valid_from: string;
  valid_to: string;
  rules: RuleFormRow[];
}

const EMPTY_RULE: RuleFormRow = {
  hours_before_departure_gte: '',
  refund_ratio_pct: '',
  fixed_fee: '',
};

const EMPTY_FORM: FormState = {
  name: '',
  description: '',
  active: true,
  valid_from: '',
  valid_to: '',
  rules: [{ ...EMPTY_RULE }],
};

function isoToLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function localInputToIso(value: string): string | undefined {
  if (!value) return undefined;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toISOString();
}

function policyToForm(policy: RefundPolicy): FormState {
  return {
    name: policy.name,
    description: policy.description ?? '',
    active: policy.active,
    valid_from: isoToLocalInput(policy.valid_from),
    valid_to: isoToLocalInput(policy.valid_to),
    rules:
      policy.time_window_rules.length > 0
        ? policy.time_window_rules.map((r) => ({
            hours_before_departure_gte: String(r.hours_before_departure_gte),
            refund_ratio_pct: String(Number(r.refund_ratio) * 100),
            fixed_fee: String(r.fixed_fee),
          }))
        : [{ ...EMPTY_RULE }],
  };
}

export function RefundPolicyEditPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id && id !== 'new');
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);

  const detailQuery = useQuery({
    queryKey: ['refund-policies.detail', id],
    queryFn: () => getRefundPolicy(id ?? ''),
    enabled: isEdit && Boolean(id),
  });

  useEffect(() => {
    if (isEdit && detailQuery.data) {
      setForm(policyToForm(detailQuery.data));
    }
  }, [isEdit, detailQuery.data]);

  const createMutation = useMutation({
    mutationFn: (dto: CreateRefundPolicyDto) => createRefundPolicy(dto),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['refund-policies.list'] });
      void queryClient.invalidateQueries({ queryKey: ['refund-policies.current'] });
      navigate('/refund-policies');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (dto: UpdateRefundPolicyDto) => updateRefundPolicy(id ?? '', dto),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['refund-policies.list'] });
      void queryClient.invalidateQueries({ queryKey: ['refund-policies.current'] });
      void queryClient.invalidateQueries({ queryKey: ['refund-policies.detail', id] });
      navigate('/refund-policies');
    },
  });

  const submitting = createMutation.isPending || updateMutation.isPending;
  const mutationError = createMutation.error ?? updateMutation.error;

  const addRule = (): void => {
    setForm((p) => ({ ...p, rules: [...p.rules, { ...EMPTY_RULE }] }));
  };

  const removeRule = (idx: number): void => {
    setForm((p) => {
      if (p.rules.length <= 1) return p; // 至少 1 條
      return { ...p, rules: p.rules.filter((_, i) => i !== idx) };
    });
  };

  const updateRule = (idx: number, patch: Partial<RuleFormRow>): void => {
    setForm((p) => ({
      ...p,
      rules: p.rules.map((r, i) => (i === idx ? { ...r, ...patch } : r)),
    }));
  };

  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault();
    setFormError(null);

    if (!form.name.trim()) {
      setFormError('請填寫政策名稱');
      return;
    }
    if (form.rules.length === 0) {
      setFormError('至少需 1 條階梯規則');
      return;
    }

    const parsedRules: RefundPolicyRule[] = [];
    for (let i = 0; i < form.rules.length; i++) {
      const r = form.rules[i]!;
      const hours = Number(r.hours_before_departure_gte);
      const pct = Number(r.refund_ratio_pct);
      const fee = Number(r.fixed_fee);
      if (!Number.isInteger(hours) || hours < 0 || hours > 24 * 365) {
        setFormError(`第 ${i + 1} 條：出航前小時數須為 0 ~ ${24 * 365} 的整數`);
        return;
      }
      if (!Number.isFinite(pct) || pct < 0 || pct > 100) {
        setFormError(`第 ${i + 1} 條：退費比例須介於 0 ~ 100`);
        return;
      }
      if (!Number.isFinite(fee) || fee < 0) {
        setFormError(`第 ${i + 1} 條：手續費須為非負數`);
        return;
      }
      const ratio = Math.round((pct / 100) * 10000) / 10000;
      if (ratio === 0 && fee === 0) {
        setFormError(`第 ${i + 1} 條：退費比例與手續費不可同時為 0（請刪除此條規則）`);
        return;
      }
      parsedRules.push({
        hours_before_departure_gte: hours,
        refund_ratio: ratio,
        fixed_fee: fee,
      });
    }

    const fromIso = localInputToIso(form.valid_from);
    const toIso = localInputToIso(form.valid_to);
    if (fromIso && toIso && new Date(toIso).getTime() <= new Date(fromIso).getTime()) {
      setFormError('生效迄日須晚於起日');
      return;
    }

    if (isEdit) {
      const dto: UpdateRefundPolicyDto = {
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        time_window_rules: parsedRules,
        active: form.active,
      };
      if (fromIso !== undefined) dto.valid_from = fromIso;
      if (toIso !== undefined) dto.valid_to = toIso;
      updateMutation.mutate(dto);
    } else {
      const dto: CreateRefundPolicyDto = {
        name: form.name.trim(),
        time_window_rules: parsedRules,
        active: form.active,
      };
      if (form.description.trim()) dto.description = form.description.trim();
      if (fromIso !== undefined) dto.valid_from = fromIso;
      if (toIso !== undefined) dto.valid_to = toIso;
      createMutation.mutate(dto);
    }
  };

  const title = useMemo(() => (isEdit ? '編輯退票政策' : '新增退票政策'), [isEdit]);

  if (isEdit && detailQuery.isLoading) {
    return <div>載入中…</div>;
  }
  if (isEdit && detailQuery.error) {
    return <div className="error-banner">{(detailQuery.error as Error).message}</div>;
  }

  return (
    <div style={{ maxWidth: 880 }}>
      <div className="page-head">
        <h1 className="page-head__title">{title}（B-2 / AC-B2-1）</h1>
        <div className="page-head__actions">
          <Link to="/refund-policies">
            <button>← 返回列表</button>
          </Link>
        </div>
      </div>

      <p style={{ color: '#6b7280', fontSize: 13 }}>
        階梯規則表示「出航前 X 小時以上 → 退 Y%（扣 Z 手續費）」；下游退票流程取「出航前小時數 ≥
        X」最大者套用。
      </p>

      <form onSubmit={handleSubmit} style={{ display: 'grid', gap: 16, marginTop: 12 }}>
        {/* 名稱 */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '140px 1fr',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <label style={{ fontSize: 13, color: '#374151' }}>政策名稱 *</label>
          <input
            type="text"
            maxLength={200}
            value={form.name}
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            required
            placeholder="例：2026 春季退票政策"
          />
        </div>

        {/* 描述 */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '140px 1fr',
            alignItems: 'flex-start',
            gap: 8,
          }}
        >
          <label style={{ fontSize: 13, color: '#374151', paddingTop: 6 }}>描述</label>
          <textarea
            rows={3}
            maxLength={2000}
            value={form.description}
            onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
            placeholder="（可選）此政策的適用情境 / 變更原因"
          />
        </div>

        {/* 生效期 */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '140px 1fr',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <label style={{ fontSize: 13, color: '#374151' }}>生效起</label>
          <input
            type="datetime-local"
            value={form.valid_from}
            onChange={(e) => setForm((p) => ({ ...p, valid_from: e.target.value }))}
          />
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '140px 1fr',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <label style={{ fontSize: 13, color: '#374151' }}>生效迄</label>
          <input
            type="datetime-local"
            value={form.valid_to}
            onChange={(e) => setForm((p) => ({ ...p, valid_to: e.target.value }))}
          />
        </div>

        {/* 啟用 */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '140px 1fr',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <label style={{ fontSize: 13, color: '#374151' }}>啟用</label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => setForm((p) => ({ ...p, active: e.target.checked }))}
            />
            <span style={{ fontSize: 13 }}>立即啟用此政策</span>
          </label>
        </div>

        {/* 階梯規則 */}
        <div style={{ marginTop: 8 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 8,
            }}
          >
            <h3 style={{ margin: 0 }}>階梯規則 *（至少 1 條）</h3>
            <button type="button" onClick={addRule}>
              + 新增階梯
            </button>
          </div>
          <table>
            <thead>
              <tr>
                <th style={{ width: 60 }}>序</th>
                <th>出航前小時數 ≥</th>
                <th>退費比例 (%)</th>
                <th>手續費 (NT$)</th>
                <th style={{ width: 80 }}>動作</th>
              </tr>
            </thead>
            <tbody>
              {form.rules.map((rule, idx) => (
                <tr key={idx}>
                  <td>{idx + 1}</td>
                  <td>
                    <input
                      type="number"
                      min={0}
                      max={24 * 365}
                      step="1"
                      value={rule.hours_before_departure_gte}
                      onChange={(e) =>
                        updateRule(idx, { hours_before_departure_gte: e.target.value })
                      }
                      required
                      placeholder="例：48"
                      style={{ width: '100%' }}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      min={0}
                      max={100}
                      step="0.01"
                      value={rule.refund_ratio_pct}
                      onChange={(e) => updateRule(idx, { refund_ratio_pct: e.target.value })}
                      required
                      placeholder="例：90 = 退 90%"
                      style={{ width: '100%' }}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      value={rule.fixed_fee}
                      onChange={(e) => updateRule(idx, { fixed_fee: e.target.value })}
                      required
                      placeholder="例：30"
                      style={{ width: '100%' }}
                    />
                  </td>
                  <td>
                    <button
                      type="button"
                      onClick={() => removeRule(idx)}
                      disabled={form.rules.length <= 1}
                    >
                      移除
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
            注意：退費比例 0% 且 手續費 0 不可同時設定（等同無規則；如不退請刪除該階梯）。
          </p>
        </div>

        {formError && <div className="error-banner">{formError}</div>}
        {mutationError && <div className="error-banner">{(mutationError as Error).message}</div>}

        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button type="submit" className="primary" disabled={submitting}>
            {submitting ? '送出中…' : isEdit ? '儲存變更' : '建立政策'}
          </button>
          <Link to="/refund-policies">
            <button type="button" disabled={submitting}>
              取消
            </button>
          </Link>
        </div>
      </form>
    </div>
  );
}
